import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";

export function useUnreadMessages(BASE_URL, socket, isConnected, messengerConversations) {
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  // If MessengerContext already has conversations loaded (Messages page), use that as the primary source
  useEffect(() => {
    try {
      if (
        Array.isArray(messengerConversations) &&
        messengerConversations.length > 0
      ) {
        const totalFromContext = messengerConversations.reduce((s, c) => {
          const u =
            c.unread ?? c.unreadCount ?? c.unread_count ?? c.unreads ?? 0;
          return s + (Number(u) || 0);
        }, 0);
        console.log(
          "[DashboardManager] Using MessengerContext unread total:",
          totalFromContext,
        );
        setUnreadMessages(totalFromContext);
      }
    } catch (err) {
      console.warn(
        "[DashboardManager] Error computing unread from MessengerContext",
        err,
      );
    }
  }, [messengerConversations]);

  // جلب عدد الرسائل غير المقروءة (مرة واحدة فقط عند التحميل)
  useEffect(() => {
    const fetchUnreadMessages = async () => {
      try {
        if (!BASE_URL) {
          console.log("[Dashboard] BASE_URL not set");
          return;
        }

        console.log(
          "[Dashboard] Fetching initial unread count from:",
          `${BASE_URL}/api/private-messages/conversations`,
        );

        const response = await apiFetch(
          `${BASE_URL}/api/private-messages/conversations`,
          { method: "GET" },
        );

        console.log(
          "[Dashboard] Response status:",
          response.status,
          response.ok,
        );

        if (response.ok) {
          const data = await response.json();
          console.log("[Dashboard] Raw API Response:", data);

          // البيانات قد تأتي كـ array مباشرة أو داخل object
          let conversations = [];
          if (Array.isArray(data)) {
            conversations = data;
          } else if (data.data && Array.isArray(data.data)) {
            conversations = data.data;
          } else if (data.conversations && Array.isArray(data.conversations)) {
            conversations = data.conversations;
          }

          console.log(
            "[Dashboard] Parsed conversations array length:",
            conversations.length,
          );

          // حساب إجمالي الرسائل غير المقروءة
          let total = 0;
          conversations.forEach((conv, index) => {
            // البحث عن unreadCount بطرق مختلفة
            const unreadCount =
              conv.unreadCount ??
              conv.unread_count ??
              conv.unreadMessagesCount ??
              conv.unreads ??
              0;

            total += Number(unreadCount) || 0;

            if (index < 3) {
              // لا نطبع جميعها، فقط أول 3
              console.log(
                `[Dashboard] Conversation: "${conv.recipientName || conv.name || "Unknown"}" - Unread: ${unreadCount}`,
              );
            }
          });

          console.log("[Dashboard] ✅ Initial unread messages count:", total);
          setUnreadMessages(total);
        } else {
          console.error(
            "[Dashboard] ❌ API returned error status:",
            response.status,
            await response.text(),
          );
        }
      } catch (error) {
        console.error("[Dashboard] ❌ Error fetching unread messages:", error);
      }
    };

    fetchUnreadMessages();
    // No polling - we'll use real-time socket events instead
  }, [BASE_URL]);

  // Real-time listener using global CustomEvents from NotificationContext
  useEffect(() => {
    console.log("[Dashboard] Setting up global real-time message listeners");

    const handleMessageReceived = (event) => {
      const data = event.detail || event;
      console.log("[Dashboard] 📩 New message received globally:", data);
      setUnreadMessages((prev) => {
        const newCount = prev + 1;
        console.log(`[Dashboard] Unread count: ${prev} → ${newCount}`);
        return newCount;
      });
      setHasNewMessage(true);
    };
    
    const handleGroupMessageReceived = (event) => {
      const data = event.detail || event;
      console.log("[Dashboard] 📩 New group message received globally:", data);
      setUnreadMessages((prev) => prev + 1);
      setHasNewMessage(true);
    };

    // Decrement counter when messages are marked as read
    const handleMessagesRead = (event) => {
      const data = event.detail || event;
      console.log("[Dashboard] ✓ Messages marked as read globally:", data);
      if (data.count) {
        setUnreadMessages((prev) => {
          const newCount = Math.max(0, prev - data.count);
          console.log(`[Dashboard] Unread count: ${prev} → ${newCount}`);
          return newCount;
        });
      }
    };

    window.addEventListener("hrms:private-message:received", handleMessageReceived);
    window.addEventListener("hrms:chat_group:message", handleGroupMessageReceived);
    window.addEventListener("hrms:private-message:read", handleMessagesRead);
    window.addEventListener("hrms:private-messages:read", handleMessagesRead);

    console.log("[Dashboard] ✅ Global real-time message listeners active");

    return () => {
      window.removeEventListener("hrms:private-message:received", handleMessageReceived);
      window.removeEventListener("hrms:chat_group:message", handleGroupMessageReceived);
      window.removeEventListener("hrms:private-message:read", handleMessagesRead);
      window.removeEventListener("hrms:private-messages:read", handleMessagesRead);
      console.log("[Dashboard] Cleaned up global message listeners");
    };
  }, []);

  return { unreadMessages, setUnreadMessages, hasNewMessage, setHasNewMessage };
}